<!--
  Full-width blue heading row on the 12-column passport grid, with edge resize.
-->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { VIcons } from '@/shared/ui'
import type { GridFieldType } from '../model/types'

const ROW_COLS = 12
const MIN_SPAN = 1

const props = defineProps<{
  field: GridFieldType
}>()

type HeadingChromePayload = {
  id: string
  row: number
  cell: number
}

const emit = defineEmits<{
  remove: [payload: HeadingChromePayload]
  resize: [payload: HeadingChromePayload & { newSpan: number }]
  rename: [payload: HeadingChromePayload & { name: string }]
}>()

const rootRef = ref<HTMLElement | null>(null)
const isResizing = ref(false)
const previewSpan = ref<number | null>(null)
const editing = ref(false)
const draft = ref(props.field.name)
const inputRef = ref<HTMLInputElement | null>(null)

watch(
  () => props.field.name,
  (n) => {
    if (!editing.value) draft.value = n
  },
)

const gridColumnStyle = computed(() => {
  const span = previewSpan.value ?? (props.field.length || ROW_COLS)
  return { gridColumn: `span ${span}` }
})

const barClass = computed(() => ({
  'pb-heading-bar--resizing': isResizing.value,
}))

function maxSpan(): number {
  return ROW_COLS - props.field.cell
}

let startX = 0
let startLen = 0
let moveHandler: ((e: PointerEvent) => void) | null = null
let upHandler: (() => void) | null = null

function teardown() {
  if (moveHandler) document.removeEventListener('pointermove', moveHandler)
  if (upHandler) document.removeEventListener('pointerup', upHandler)
  moveHandler = null
  upHandler = null
}

function onResizePointerDown(event: PointerEvent) {
  event.preventDefault()
  event.stopPropagation()

  isResizing.value = true
  startX = event.clientX
  startLen = props.field.length || ROW_COLS
  previewSpan.value = startLen

  const rowEl = rootRef.value?.closest('.pb-grid-row') as HTMLElement | null
  const containerWidth = rowEl?.getBoundingClientRect().width ?? 0
  const colW = containerWidth > 0 ? containerWidth / ROW_COLS : 1

  moveHandler = (e: PointerEvent) => {
    if (!colW) return
    const deltaCols = Math.round((e.clientX - startX) / colW)
    previewSpan.value = Math.max(MIN_SPAN, Math.min(maxSpan(), startLen + deltaCols))
  }

  upHandler = () => {
    teardown()
    const finalSpan = previewSpan.value ?? startLen
    previewSpan.value = null
    isResizing.value = false
    const clamped = Math.max(MIN_SPAN, Math.min(maxSpan(), finalSpan))
    if (clamped !== (props.field.length || ROW_COLS)) {
      emit('resize', {
        id: props.field.id,
        row: props.field.row,
        cell: props.field.cell,
        newSpan: clamped,
      })
    }
  }

  document.addEventListener('pointermove', moveHandler)
  document.addEventListener('pointerup', upHandler)
}

function startEdit() {
  editing.value = true
  draft.value = props.field.name
  nextTick(() => {
    inputRef.value?.focus()
    inputRef.value?.select()
  })
}

function cancel() {
  editing.value = false
  draft.value = props.field.name
}

function commit() {
  if (!editing.value) return
  const n = draft.value.trim()
  if (!n) {
    cancel()
    return
  }
  editing.value = false
  if (n !== props.field.name) {
    emit('rename', {
      id: props.field.id,
      row: props.field.row,
      cell: props.field.cell,
      name: n,
    })
  }
}
</script>

<template>
  <div
    ref="rootRef"
    class="pb-heading-bar pb-heading-bar--blue"
    :class="barClass"
    :style="gridColumnStyle"
  >
    <input
      v-if="editing"
      ref="inputRef"
      v-model="draft"
      class="pb-heading-bar__input"
      type="text"
      aria-label="Rename heading"
      @blur="commit"
      @keydown.enter.prevent="commit"
      @keydown.escape.prevent="cancel"
      @click.stop
    />
    <span
      v-else
      class="pb-heading-bar__title"
      :title="field.name"
      @dblclick.prevent="startEdit"
    >{{ field.name }}</span>
    <VBtn
      class="pb-heading-delete"
      icon
      variant="text"
      size="x-small"
      color="white"
      density="compact"
      aria-label="Удалить заголовок"
      @click.stop="emit('remove', { id: field.id, row: field.row, cell: field.cell })"
    >
      <VIcons type="close" />
    </VBtn>
    <div
      class="pb-heading-resize"
      @pointerdown="onResizePointerDown"
    />
  </div>
</template>

<style scoped lang="scss">
.pb-heading-resize {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;
  border-radius: 0 4px 4px 0;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
}
</style>
